import type { PartStockParams } from "@common/api/part-stock"
import type { PartStockView } from "@common/client"
import {
  PartStockSortSchema,
  SortDirectionSchema,
} from "@common/client/schemas.gen"
import { z } from "zod"
import {
  type Column,
  columnSchema,
  DEFAULT_COLUMNS,
  HOLDER_COLUMN,
  withPartNumber,
} from "./columns"
import { type Grouping, groupChoiceSchema, groupingSchema } from "./grouping"
import { conditionSchema, stockStatusSchema } from "./stock"

const VIEWS = [
  "in_stock",
  "needs_attention",
  "reserved",
  "rotable",
  "consumable",
] as const satisfies readonly PartStockView[]

const sortSchema = z.enum(PartStockSortSchema.enum)

export type StockSort = z.infer<typeof sortSchema>

const directionSchema = z.enum(SortDirectionSchema.enum)

export const TABS = ["all", ...VIEWS, "archived"] as const

const tabSchema = z.enum(TABS)

export type Tab = z.infer<typeof tabSchema>

export const inventorySearchSchema = z.object({
  tab: tabSchema.optional().catch(undefined),
  search: z.string().optional().catch(undefined),
  status: z.array(stockStatusSchema).optional().catch(undefined),
  manufacturer: z.array(z.string()).optional().catch(undefined),
  condition: z.array(conditionSchema).optional().catch(undefined),
  facility: z.array(z.string()).optional().catch(undefined),
  work_order: z.array(z.string()).optional().catch(undefined),
  group: groupChoiceSchema.optional().catch(undefined),
  subgroup: groupingSchema.optional().catch(undefined),
  columns: z.array(columnSchema).optional().catch(undefined),
  sort: sortSchema.optional().catch(undefined),
  direction: directionSchema.optional().catch(undefined),
})

export type InventorySearch = z.infer<typeof inventorySearchSchema>

export type InventoryDisplay = {
  group: Grouping | undefined
  subgroup: Grouping | undefined
  columns: Column[]
  sort: StockSort
  direction: z.infer<typeof directionSchema>
}

export function tabLabel(tab: Tab): string {
  switch (tab) {
    case "all":
      return "All"
    case "in_stock":
      return "In stock"
    case "needs_attention":
      return "Needs attention"
    case "reserved":
      return "Reserved"
    case "rotable":
      return "Rotables"
    case "consumable":
      return "Consumables"
    case "archived":
      return "Archived"
  }
}

const RESERVED_COLUMNS: Column[] = [...DEFAULT_COLUMNS, HOLDER_COLUMN]

export function defaultColumns(tab: Tab | undefined): Column[] {
  return tab === "reserved" ? RESERVED_COLUMNS : DEFAULT_COLUMNS
}

function viewOf(tab: Tab | undefined): PartStockView | undefined {
  switch (tab) {
    case undefined:
    case "all":
    case "archived":
      return undefined
    default:
      return tab
  }
}

export function stockParams(search: InventorySearch): PartStockParams {
  const { group, subgroup, sort, direction } = displayOf(search)
  return {
    search: search.search ?? "",
    view: viewOf(search.tab),
    status: search.status,
    manufacturer: search.manufacturer,
    condition: search.condition,
    facilityId: search.facility,
    workPackageId: search.work_order,
    archive: search.tab === "archived" ? "archived" : "live",
    group,
    subgroup,
    sort,
    direction,
  }
}

export function displayOf(search: InventorySearch): InventoryDisplay {
  const group =
    search.group === "none" ? undefined : (search.group ?? "availability")
  return {
    group,
    subgroup: group && search.subgroup !== group ? search.subgroup : undefined,
    columns: search.columns
      ? withPartNumber(search.columns)
      : defaultColumns(search.tab),
    sort: search.sort ?? "number",
    direction: search.direction ?? "asc",
  }
}
